<template>
  <v-dialog v-model="showDialog" persistent max-width="500px">
    <v-card>
      <v-card-text>
        <div class="md-subheader-no-spacing">Confirm Address</div>
        <div
          class="md-body-no-spacing"
          v-if="addressError"
          data-testid="validate-address-unverified-msg"
        >
          We couldn’t verify your address. Please make sure it’s valid before continuing.
        </div>
        <div class="md-body-no-spacing" v-else-if="showDialog">
          We've found the address you were looking for. Does this look right?
        </div>  
        <div
          class="md-body-no-spacing mt-2"
          v-if="!addressError && zipOrStateChanged"
          data-testid="validate-address-zip-changed-msg"
        >
          <strong>The ZIP code or state changed.</strong>
          The address you entered doesn't match that ZIP code or state. Sales
          tax and shipping are based on the address you choose, so please check
          the Validated Address before using it.
        </div>
        <div class="py-3 px-3">
          <address>
            <h4>Original Address</h4>
            {{ displayOriginal.street_and_number }}
            {{ displayOriginal.suite_unit_etc }}
            <br />
            {{ displayOriginal.city }}, {{ displayOriginal.region }},
            {{ displayOriginal.zip_code }}
            <br />
            {{ displayOriginal.country }}
          </address>
          <address v-if="!addressError">
            <h4>Validated Address</h4>
            {{ validatedAddress.street_and_number }}
            {{ validatedAddress.suite_unit_etc }}
            <br />
            {{ validatedAddress.city }}, {{ validatedAddress.region }},
            {{ validatedAddress.zip_code }}
            <br />
            {{ validatedAddress.country }}
          </address> 
        </div>             
        <span v-if="!hideMessage" class="mt-3 mb-3">
          <i v-if="message">
            {{ message }}
          </i>
          <i v-else>
            * Using an unverified address could cause problems with shipping and
            tax calculation
          </i>
        </span>
      </v-card-text>
      <v-card-actions class="py-3 px-3">
        <v-btn
          variant="flat"
          @click="showDialog = false"
          data-testid="edit-original-address-btn"
        >
          Edit
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          class="text-white"
          variant="flat"
          @click="useOriginalAddress"
          data-testid="replace-address-btn"
        >
          Use Original
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          class="text-white"
          variant="flat"
          @click="useValidatedAddress"
          data-testid="replace-address-btn"
          v-if="!addressError"
        >
          Use Validated
        </v-btn>
      </v-card-actions>
    </v-card>
    <!-- Empty card to show as dialog is closing to keep persistent dialog close animation -->
    <v-card width="500" height="150" v-if="!addressError && !showDialog">
    </v-card>
  </v-dialog>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import { UNPARSEABLE_ADDRESS_MESSAGE } from "@/utils/addressParse";
import rules from "@/mixins/rules";
import helpers from "@/mixins/helpers";
import moment from "moment";
import _ from "lodash";
import { mapGetters } from "vuex";

export default {
  props: {
    originalLocation: { required: true },
    message: { required: false },
    hideMessage: { required: false },
  },
  watch: {
    showDialog: function () {
      //if dialog opened, load component
      if (this.showDialog) {
        //this.load();
      }
      //if dialog closed
      else {
        this.addressError = false;
      }
    },
  },

  computed: {
    //    ...mapGetters(["getStates", "getCountries"]),

    // The back office's corrected address is in another state or 5-digit ZIP
    // than the one typed (card #628). Shown beside both addresses so the
    // shopper chooses; the flag never leaves this component.
    zipOrStateChanged() {
      return Boolean(this.validatedAddress?.zip_or_state_changed);
    },

    // ``originalLocation`` may be a structured address object (store /
    // vendor callers) OR a plain string (DistributorAddEdit's single
    // "Physical address" text field), and may momentarily be undefined
    // (e.g. after the parent replaces its distributor with the create
    // response, which carries no ``address`` field). The template reads
    // per-field properties, so normalize to a safe object here to avoid
    // "Cannot read properties of undefined" render crashes. When the source
    // is a string, surface it as the street line so the "Original Address"
    // block still shows something meaningful.
    displayOriginal() {
      const loc = this.originalLocation;
      if (loc && typeof loc === "object") {
        return {
          street_and_number: loc.street_and_number || "",
          suite_unit_etc: loc.suite_unit_etc || "",
          city: loc.city || "",
          region: loc.region || "",
          zip_code: loc.zip_code || "",
          country: loc.country || "",
        };
      }
      return {
        street_and_number: typeof loc === "string" ? loc : "",
        suite_unit_etc: "",
        city: "",
        region: "",
        zip_code: "",
        country: "",
      };
    },
  },

  data() {
    return {
      addressError: false,
      showDialog: false,
      validatedAddress: [],
    };
  },
  mounted() {},
  methods: {
    load() {},

    locationToString(location) {
      let formatted = "";
      formatted += location.street_and_number + " ";
      formatted += location.suite_unit_etc ? location.suite_unit_etc + " " : "";
      formatted += location.city + " ";
      formatted += location.region + " ";
      formatted += location.country + " ";
      formatted += location.zip_code;
      return formatted;
    },

    async parseAndValidateAddress() {
      let location = JSON.parse(JSON.stringify(this.originalLocation));
      let response;

      let formattedLocation =
        typeof location != "string"
          ? this.locationToString(location)
          : location;

      try {
        response = await this.$axios.post(
          "/address_validation/parseAndValidateAddress",
          {
            location: formattedLocation,
          }
        );
      } catch (err) {
        console.log(describeError(err));
        // Address validation unavailable — save with original address directly.
        this.$store.dispatch("raiseInfo", "Address validation is currently unavailable. Address saved without verification.");
        this._savedViaFallback = true;
        this.$emit("verificationUnavailable");
        this.$emit("useOriginalAddress");
        return;
      }
      
      // Handle successful response
      if (response && response.data) {
        if (response.data._unparseable === true) {
          // The server could not read the pasted text into fields. Do NOT
          // save it as an address (no useOriginalAddress, no blob in
          // street_and_number): tell the user what to enter and stop.
          this.$store.dispatch(
            "raiseError",
            response.data._note || UNPARSEABLE_ADDRESS_MESSAGE
          );
          this._unparseable = true;
          this.$emit("addressUnparseable");
        } else if (response.data.error) {
          // Backend validation failed but returned structured error - show dialog to user
          this.validatedAddress = this.originalLocation;
          this.showErrorDialog();
          //could not validate address
          //show couldn't validate popup and assign the orignal address to the validated address
        } else if (response.data._unmatched === true) {
          // The service ANSWERED that it cannot match the parsed address (the
          // same answer validateAddress handles below): warn, and let the
          // user edit or keep it. Checked before ``_fallback``, which an
          // unmatched answer also carries. Never blocks: Use Original saves.
          this.validatedAddress = this.originalLocation;
          this.$emit("addressUnmatched");
          this.showErrorDialog();
        } else if (response.data._fallback === true) {
          // Address validation service unavailable — save with original address directly.
          this.$store.dispatch("raiseInfo", "Address validation is currently unavailable. Address saved without verification.");
          this._savedViaFallback = true;
          this.$emit("verificationUnavailable");
          this.$emit("useOriginalAddress");
          return;
        } else {
          this.validatedAddress = response.data;
          //show confirm validation popup
        }
      } else {
        // No response data — save with original address directly.
        this._savedViaFallback = true;
        this.$emit("verificationUnavailable");
        this.$emit("useOriginalAddress");
      }
    },

    async validateAddress() {
      let location = JSON.parse(JSON.stringify(this.originalLocation));

      //set state abbreviation for address validation
      location.stateAbbreviation = location.region;
      //set country abbreviation for address validation
      location.countryAbbreviation = location.country;

      let response;
      try {
        response = await this.$axios.post(
          "/address_validation/validateAddress",
          {
            location: location,
          }
        );
        if (response.data && response.data.error) {
          this.validatedAddress = this.originalLocation;
          this.showErrorDialog();
        } else if (response.data && response.data._unmatched === true) {
          // The service ANSWERED and could not match this address: it is
          // the address that is wrong, not the service. Ask the user to
          // edit it or keep it as entered, rather than claiming validation
          // is unavailable. Checked before ``_fallback``, which it also
          // carries. ``addressUnmatched`` lets checkout mark the order.
          this.validatedAddress = this.originalLocation;
          this.$emit("addressUnmatched");
          this.showErrorDialog();
        } else if (response.data && response.data._fallback === true) {
          // Address validation service unavailable — save with original address directly.
          this.$store.dispatch("raiseInfo", "Address validation is currently unavailable. Address saved without verification.");
          this._savedViaFallback = true;
          this.$emit("verificationUnavailable");
          this.$emit("useOriginalAddress");
          return;
        } else {
          this.validatedAddress = response.data;
        }
      } catch (err) {
        console.log(describeError(err));
        // Address validation failed — fall back to saving with original address
        this.$store.dispatch("raiseInfo", "Address validation is currently unavailable. Address saved without verification.");
        this._savedViaFallback = true;
        this.$emit("verificationUnavailable");
        this.$emit("useOriginalAddress");
      }
    },

    showErrorDialog() {
      this.addressError = true;
      this.showDialog = true;
      // Callers (SignUpModule, ShopperDetails) clear
      // their loading flag on this event; the error mode must send it too,
      // or Save / Next / the address fields stay disabled after Edit.
      this.$emit("dialogOpened");
    },

    async show() {
      // Reset dialog state
      this.showDialog = false;
      this._savedViaFallback = false;
      this._unparseable = false;

      if (this.originalLocation.city) {
        await this.validateAddress();
      } else {
        await this.parseAndValidateAddress();
      }

      // Only show dialog if validation didn't auto-save via fallback, and the
      // text was readable (an unparseable address stops with a message).
      if (!this.showDialog && !this._savedViaFallback && !this._unparseable) {
        this.showDialog = true;
        this.$emit("dialogOpened");
      }
    },

    useValidatedAddress() {
      // The flag is display-only: callers spread this into their models.
      const address = _.omit(this.validatedAddress, "zip_or_state_changed");
      this.$emit("useValidatedAddress", address);
      this.validatedAddress = {};
      this.showDialog = false;
    },

    useOriginalAddress() {
      this.$emit("useOriginalAddress");
      this.showDialog = false;
    },
  },
};
</script>
<style scoped></style>
